// Component to manage and view all accounts in the system for superadmins

import { useState, useEffect } from 'react'
import { useToast } from "@/components/ui/use-toast"
import { Button } from "@/components/ui/button"
import { Users, Search, RefreshCw, Building2, User, Plus, Minus, History, Coins, Trash2, AlertTriangle, Tag } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Input } from "@/components/ui/input"
import { supabase } from '@/lib/supabase'
import { Checkbox } from "@/components/ui/checkbox"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { useRole } from '@/lib/useRole'
import { format } from 'date-fns'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog"
import { Database } from '../../../database.types'
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

type Account = Database['basejump']['Tables']['accounts']['Row']
type AccountUser = Database['basejump']['Tables']['account_user']['Row']
type BillingCustomer = Database['basejump']['Tables']['billing_customers']['Row']
type Brand = Database['public']['Tables']['brands']['Row']

interface AccountWithDetails extends Account {
    account_users?: AccountUser[]
    billing_customers?: BillingCustomer[]
    user_count?: number
}

interface CreditBalance {
    plan_credits: number
    purchased_credits: number
    total_credits: number
}

interface CreditTransaction {
    id: string
    account_id: string
    amount: number
    balance_after: number
    type: 'plan_allocation' | 'purchase' | 'consumption' | 'refund' | 'adjustment'
    category: string | null
    description: string
    metadata: any
    created_at: string
}

export default function AccountsManagement() {
    const [accounts, setAccounts] = useState<AccountWithDetails[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedAccount, setSelectedAccount] = useState<AccountWithDetails | null>(null)
    const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null)
    const [creditTransactions, setCreditTransactions] = useState<CreditTransaction[]>([])
    const [creditDialogOpen, setCreditDialogOpen] = useState(false)
    const [creditOperation, setCreditOperation] = useState<'add' | 'deduct'>('add')
    const [creditAmount, setCreditAmount] = useState('')
    const [creditReason, setCreditReason] = useState('')
    const [creditLoading, setCreditLoading] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deleteConfirmation, setDeleteConfirmation] = useState('')
    const [deleteCheckbox, setDeleteCheckbox] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [accountToDelete, setAccountToDelete] = useState<AccountWithDetails | null>(null)
    const [deletionStats, setDeletionStats] = useState<{
        brands: number
        campaigns: number
        contents: number
        credits: number
    } | null>(null)
    const [brands, setBrands] = useState<Brand[]>([])
    const [brandsLoading, setBrandsLoading] = useState(false)
    const { toast } = useToast()
    const { isSuperAdmin } = useRole()

    const fetchAccounts = async () => {
        try {
            setLoading(true)
            
            // Fetch accounts
            const { data: accountsData, error } = await supabase
                .schema('basejump')
                .from('accounts')
                .select('*')
                .order('created_at', { ascending: false })

            if (error) throw error

            // Fetch related data for each account
            const accountsWithDetails: AccountWithDetails[] = await Promise.all(
                (accountsData || []).map(async (account) => {
                    // Fetch account users
                    const { data: users } = await supabase
                        .schema('basejump')
                        .from('account_user')
                        .select('*')
                        .eq('account_id', account.id)

                    // Fetch billing info
                    const { data: billing } = await supabase
                        .schema('basejump')
                        .from('billing_customers')
                        .select('*')
                        .eq('account_id', account.id)

                    return {
                        ...account,
                        account_users: users || [],
                        billing_customers: billing || [],
                        user_count: users?.length || 0
                    }
                })
            )

            setAccounts(accountsWithDetails)
        } catch (error) {
            console.error('Error fetching accounts:', error)
            toast({
                title: "Error fetching accounts",
                description: "Could not load the accounts list. Please try again.",
                variant: "destructive",
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (isSuperAdmin) {
            fetchAccounts()
        }
    }, [isSuperAdmin])

    const fetchCreditBalance = async (accountId: string) => {
        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session) return

            const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/admin/credits/balance/${accountId}`, {
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                }
            })

            if (!response.ok) throw new Error('Failed to fetch credit balance')
            
            const balance = await response.json() as CreditBalance
            setCreditBalance(balance)
        } catch (error) {
            console.error('Error fetching credit balance:', error)
            toast({
                title: "Error fetching credit balance",
                description: "Could not load the credit balance.",
                variant: "destructive",
            })
        }
    }

    const fetchCreditTransactions = async (accountId: string) => {
        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session) return

            const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/admin/credits/transactions/${accountId}`, {
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                }
            })

            if (!response.ok) throw new Error('Failed to fetch credit transactions')
            
            const result = await response.json() as { transactions: CreditTransaction[], total: number, limit: number, offset: number }
            setCreditTransactions(result.transactions || [])
        } catch (error) {
            console.error('Error fetching credit transactions:', error)
            toast({
                title: "Error fetching transactions",
                description: "Could not load the transaction history.",
                variant: "destructive",
            })
        }
    }

    const fetchBrands = async (accountId: string) => {
        try {
            setBrandsLoading(true)
            
            const { data: brandsData, error } = await supabase
                .from('brands')
                .select('*')
                .eq('account_id', accountId)
                .order('created_at', { ascending: false })

            if (error) throw error

            setBrands(brandsData || [])
        } catch (error) {
            console.error('Error fetching brands:', error)
            toast({
                title: "Error fetching brands",
                description: "Could not load the brands list.",
                variant: "destructive",
            })
        } finally {
            setBrandsLoading(false)
        }
    }

    const handleCreditOperation = async () => {
        if (!selectedAccount || !creditAmount || !creditReason) {
            toast({
                title: "Missing information",
                description: "Please provide amount and reason.",
                variant: "destructive",
            })
            return
        }

        setCreditLoading(true)

        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session) return

            const endpoint = creditOperation === 'add' ? 'add' : 'deduct'
            const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/admin/credits/${endpoint}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountId: selectedAccount.id,
                    amount: parseInt(creditAmount),
                    reason: creditReason
                })
            })

            const result = await response.json() as { 
                success?: boolean, 
                message?: string, 
                error?: string, 
                newBalance?: CreditBalance 
            }

            if (!response.ok) {
                throw new Error(result.error || 'Failed to update credits')
            }

            toast({
                title: "Credits updated",
                description: result.message || "Credits updated successfully",
            })

            if (result.newBalance) {
                setCreditBalance(result.newBalance)
            }
            setCreditDialogOpen(false)
            setCreditAmount('')
            setCreditReason('')
            
            // Refresh transactions
            fetchCreditTransactions(selectedAccount.id)
        } catch (error) {
            console.error('Error updating credits:', error)
            toast({
                title: "Error updating credits",
                description: error instanceof Error ? error.message : "Failed to update credits",
                variant: "destructive",
            })
        } finally {
            setCreditLoading(false)
        }
    }

    const filteredAccounts = accounts.filter(account =>
        account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.id.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const handleViewDetails = async (account: AccountWithDetails) => {
        setSelectedAccount(account)
        // Fetch credit info and brands when viewing details
        fetchCreditBalance(account.id)
        fetchCreditTransactions(account.id)
        fetchBrands(account.id)
    }

    const fetchDeletionStats = async (account: AccountWithDetails) => {
        try {
            // Fetch brands count
            const { count: brandCount } = await supabase
                .from('brands')
                .select('*', { count: 'exact', head: true })
                .eq('account_id', account.id)

            // Fetch brand IDs for further queries
            const { data: brandIds } = await supabase
                .from('brands')
                .select('id')
                .eq('account_id', account.id)

            let campaignCount = 0
            let contentCount = 0

            if (brandIds && brandIds.length > 0) {
                const ids = brandIds.map(b => b.id)
                
                // Fetch campaigns count
                const { count: campaigns } = await supabase
                    .from('campaigns')
                    .select('*', { count: 'exact', head: true })
                    .in('brand_id', ids)
                campaignCount = campaigns || 0

                // Fetch contents count
                const { count: contents } = await supabase
                    .from('contents')
                    .select('*', { count: 'exact', head: true })
                    .in('brand_id', ids)
                contentCount = contents || 0
            }

            // Fetch credit balance
            const { data: creditBalanceData } = await supabase
                .schema('basejump')
                .rpc('get_credit_balance', { p_account_id: account.id })

            const totalCredits = (creditBalanceData && creditBalanceData[0]?.total_credits) || 0

            setDeletionStats({
                brands: brandCount || 0,
                campaigns: campaignCount,
                contents: contentCount,
                credits: totalCredits
            })
        } catch (error) {
            console.error('Error fetching deletion stats:', error)
        }
    }

    const handleInitiateDelete = async (account: AccountWithDetails) => {
        setAccountToDelete(account)
        setDeleteConfirmation('')
        setDeleteCheckbox(false)
        setDeletionStats(null)
        setDeleteDialogOpen(true)
        
        // Fetch stats for the confirmation dialog
        await fetchDeletionStats(account)
    }

    const handleDeleteAccount = async () => {
        if (!accountToDelete || !deleteCheckbox) return
        
        const expectedName = accountToDelete.name || accountToDelete.slug || ''
        if (deleteConfirmation.toLowerCase() !== expectedName.toLowerCase()) {
            toast({
                title: "Confirmation name doesn't match",
                description: "Please type the exact account name to confirm deletion.",
                variant: "destructive",
            })
            return
        }

        setDeleteLoading(true)

        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session) throw new Error('No session found')

            const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/admin/accounts/delete`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountId: accountToDelete.id,
                    confirmationName: deleteConfirmation
                })
            })

            const result = await response.json()

            if (!response.ok) {
                throw new Error(result.error || 'Failed to delete account')
            }

            toast({
                title: "Account deleted successfully",
                description: result.message || "The account and all associated data have been permanently deleted.",
            })

            // Close dialogs and refresh the list
            setDeleteDialogOpen(false)
            setSelectedAccount(null)
            setAccountToDelete(null)
            fetchAccounts()

        } catch (error) {
            console.error('Error deleting account:', error)
            toast({
                title: "Failed to delete account",
                description: error instanceof Error ? error.message : "An error occurred while deleting the account.",
                variant: "destructive",
            })
        } finally {
            setDeleteLoading(false)
        }
    }

    if (!isSuperAdmin) {
        return (
            <Alert>
                <AlertDescription>
                    You need to be a superadmin to view accounts.
                </AlertDescription>
            </Alert>
        )
    }

    return (
        <div>
            <h2 className="text-xl font-bold mb-4">Accounts Management</h2>
            
            <div className="flex gap-4 mb-6">
                <div className="flex-1">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by name, slug, or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                </div>
                <Button
                    onClick={fetchAccounts}
                    disabled={loading}
                    variant="outline"
                >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Account</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Users</TableHead>
                            <TableHead>Plan</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center">
                                    Loading accounts...
                                </TableCell>
                            </TableRow>
                        ) : filteredAccounts.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center">
                                    No accounts found
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredAccounts.map((account) => (
                                <TableRow key={account.id}>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {account.personal_account ? (
                                                <User className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                            )}
                                            <div>
                                                <div className="font-medium">{account.name || 'Unnamed'}</div>
                                                <div className="text-sm text-muted-foreground">{account.slug || account.id}</div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {account.personal_account ? 'Personal' : 'Team'}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Users className="h-4 w-4 text-muted-foreground" />
                                            {account.user_count}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {account.billing_customers?.[0]?.plan_id || '-'}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div className={`h-2 w-2 rounded-full ${
                                                account.billing_customers?.[0]?.billing_status === 'active' || account.billing_customers?.[0]?.billing_status === 'trialing'
                                                    ? 'bg-green-500' 
                                                    : 'bg-gray-300'
                                            }`} />
                                            {account.billing_customers?.[0]?.billing_status || 'No billing'}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {account.created_at 
                                            ? format(new Date(account.created_at), 'MMM d, yyyy')
                                            : '-'
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleViewDetails(account)}
                                        >
                                            View Details
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Account Details Dialog */}
            <Dialog open={!!selectedAccount} onOpenChange={() => {
                setSelectedAccount(null)
                setCreditBalance(null)
                setCreditTransactions([])
                setBrands([])
            }}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader className="flex flex-row items-center justify-between">
                        <DialogTitle>Account Details</DialogTitle>
                        {selectedAccount && (
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleInitiateDelete(selectedAccount)}
                                className="ml-auto"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Account
                            </Button>
                        )}
                    </DialogHeader>
                    {selectedAccount && (
                        <Tabs defaultValue="details" className="flex-1 overflow-hidden flex flex-col">
                            <TabsList className="grid w-full grid-cols-4">
                                <TabsTrigger value="details">Account Details</TabsTrigger>
                                <TabsTrigger value="brands" className="flex items-center gap-2">
                                    <Tag className="h-4 w-4" />
                                    Brands
                                </TabsTrigger>
                                <TabsTrigger value="credits" className="flex items-center gap-2">
                                    <Coins className="h-4 w-4" />
                                    Credits
                                </TabsTrigger>
                                <TabsTrigger value="transactions" className="flex items-center gap-2">
                                    <History className="h-4 w-4" />
                                    Transactions
                                </TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="details" className="mt-4 space-y-4 overflow-y-auto">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground">Account ID</p>
                                        <p className="font-mono text-sm">{selectedAccount.id}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground">Name</p>
                                        <p>{selectedAccount.name || 'Unnamed'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground">Slug</p>
                                        <p>{selectedAccount.slug || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground">Type</p>
                                        <p>{selectedAccount.personal_account ? 'Personal' : 'Team'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground">Created</p>
                                        <p>{selectedAccount.created_at 
                                            ? format(new Date(selectedAccount.created_at), 'MMM d, yyyy h:mm a')
                                            : '-'
                                        }</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground">Updated</p>
                                        <p>{selectedAccount.updated_at
                                            ? format(new Date(selectedAccount.updated_at), 'MMM d, yyyy h:mm a')
                                            : '-'
                                        }</p>
                                    </div>
                                </div>

                                {selectedAccount.billing_customers?.[0] && (
                                    <div className="border-t pt-4">
                                        <h4 className="font-medium mb-2">Billing Information</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-sm font-medium text-muted-foreground">Status</p>
                                                <p>{selectedAccount.billing_customers[0].billing_status || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-muted-foreground">Plan</p>
                                                <p>{selectedAccount.billing_customers[0].plan_id || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-muted-foreground">Subscription ID</p>
                                                <p className="font-mono text-sm">
                                                    {selectedAccount.billing_customers[0].billing_subscription_id || '-'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-muted-foreground">Period End</p>
                                                <p>{selectedAccount.billing_customers[0].billing_period_end
                                                    ? format(new Date(selectedAccount.billing_customers[0].billing_period_end), 'MMM d, yyyy')
                                                    : '-'
                                                }</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-2">Users ({selectedAccount.account_users?.length || 0})</h4>
                                    <div className="space-y-2">
                                        {selectedAccount.account_users?.map((user) => (
                                            <div key={user.user_id} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                                                <span className="font-mono text-sm">{user.user_id}</span>
                                                <span className="text-sm font-medium">{user.account_role}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </TabsContent>
                            
                            <TabsContent value="brands" className="mt-4 space-y-4 overflow-y-auto">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-medium">Brands ({brands.length})</h4>
                                        <Button
                                            onClick={() => selectedAccount && fetchBrands(selectedAccount.id)}
                                            disabled={brandsLoading}
                                            variant="outline"
                                            size="sm"
                                        >
                                            <RefreshCw className={`h-4 w-4 mr-2 ${brandsLoading ? 'animate-spin' : ''}`} />
                                            Refresh
                                        </Button>
                                    </div>
                                    
                                    {brandsLoading ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            Loading brands...
                                        </div>
                                    ) : brands.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            No brands found for this account
                                        </div>
                                    ) : (
                                        <div className="rounded-md border">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Brand Name</TableHead>
                                                        <TableHead>Status</TableHead>
                                                        <TableHead>Niche</TableHead>
                                                        <TableHead>B2B/B2C</TableHead>
                                                        <TableHead>Created</TableHead>
                                                        <TableHead>Onboarding</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {brands.map((brand) => (
                                                        <TableRow key={brand.id}>
                                                            <TableCell>
                                                                <div className="flex items-center gap-2">
                                                                    <Tag className="h-4 w-4 text-muted-foreground" />
                                                                    <div>
                                                                        <div className="font-medium">{brand.brand_name}</div>
                                                                        <div className="text-sm text-muted-foreground font-mono">
                                                                            {brand.id}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge 
                                                                    variant={brand.status === 'ACTIVE' ? 'default' : 'secondary'}
                                                                >
                                                                    {brand.status}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="max-w-[200px] truncate">
                                                                    {brand.niche || '-'}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge variant="outline">
                                                                    {brand.b2b_b2c}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell>
                                                                {brand.created_at 
                                                                    ? format(new Date(brand.created_at), 'MMM d, yyyy')
                                                                    : '-'
                                                                }
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="flex items-center gap-2">
                                                                    <div className={`h-2 w-2 rounded-full ${
                                                                        brand.onboarding_completed 
                                                                            ? 'bg-green-500' 
                                                                            : 'bg-yellow-500'
                                                                    }`} />
                                                                    <span className="text-sm">
                                                                        {brand.onboarding_completed ? 'Complete' : `Step ${brand.onboarding_step || 0}`}
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}
                                </div>
                            </TabsContent>
                            
                            <TabsContent value="credits" className="mt-4 space-y-4">
                                {creditBalance ? (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <Card>
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-sm font-medium">Plan Credits</CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                    <p className="text-2xl font-bold">{(creditBalance.plan_credits ?? 0).toLocaleString()}</p>
                                                </CardContent>
                                            </Card>
                                            <Card>
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-sm font-medium">Purchased Credits</CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                    <p className="text-2xl font-bold">{(creditBalance.purchased_credits ?? 0).toLocaleString()}</p>
                                                </CardContent>
                                            </Card>
                                            <Card>
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-sm font-medium">Total Credits</CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                    <p className="text-2xl font-bold text-primary">{(creditBalance.total_credits ?? 0).toLocaleString()}</p>
                                                </CardContent>
                                            </Card>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                            <Button 
                                                onClick={() => {
                                                    setCreditOperation('add')
                                                    setCreditDialogOpen(true)
                                                }}
                                                variant="outline"
                                                className="flex items-center gap-2"
                                            >
                                                <Plus className="h-4 w-4" />
                                                Add Credits
                                            </Button>
                                            <Button 
                                                onClick={() => {
                                                    setCreditOperation('deduct')
                                                    setCreditDialogOpen(true)
                                                }}
                                                variant="outline"
                                                className="flex items-center gap-2"
                                            >
                                                <Minus className="h-4 w-4" />
                                                Deduct Credits
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        Loading credit balance...
                                    </div>
                                )}
                            </TabsContent>
                            
                            <TabsContent value="transactions" className="mt-4 overflow-y-auto">
                                <div className="space-y-2">
                                    {creditTransactions.length > 0 ? (
                                        creditTransactions.map((transaction) => (
                                            <div key={transaction.id} className="border rounded-lg p-4 space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        {transaction.amount > 0 ? (
                                                            <Plus className="h-4 w-4 text-green-600" />
                                                        ) : (
                                                            <Minus className="h-4 w-4 text-red-600" />
                                                        )}
                                                        <span className={`font-semibold ${transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                            {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()} credits
                                                        </span>
                                                    </div>
                                                    <Badge variant="outline">{transaction.type}</Badge>
                                                </div>
                                                <p className="text-sm">{transaction.description}</p>
                                                <div className="flex items-center justify-between text-sm text-muted-foreground">
                                                    <span>Balance after: {transaction.balance_after.toLocaleString()}</span>
                                                    <span>{format(new Date(transaction.created_at), 'MMM d, yyyy h:mm a')}</span>
                                                </div>
                                                {transaction.metadata?.adjusted_by_email && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Adjusted by: {transaction.metadata.adjusted_by_email}
                                                    </p>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            No transactions found
                                        </div>
                                    )}
                                </div>
                            </TabsContent>
                        </Tabs>
                    )}
                </DialogContent>
            </Dialog>

            {/* Credit Operation Dialog */}
            <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {creditOperation === 'add' ? 'Add Credits' : 'Deduct Credits'}
                        </DialogTitle>
                        <DialogDescription>
                            {creditOperation === 'add' 
                                ? 'Add credits to this account. These will be added as purchased credits.'
                                : 'Deduct credits from this account. Credits will be deducted from plan credits first.'
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="amount">Amount</Label>
                            <Input
                                id="amount"
                                type="number"
                                placeholder="Enter amount"
                                value={creditAmount}
                                onChange={(e) => setCreditAmount(e.target.value)}
                                min="1"
                            />
                        </div>
                        <div>
                            <Label htmlFor="reason">Reason</Label>
                            <Textarea
                                id="reason"
                                placeholder="Enter reason for this adjustment"
                                value={creditReason}
                                onChange={(e) => setCreditReason(e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setCreditDialogOpen(false)
                                setCreditAmount('')
                                setCreditReason('')
                            }}
                            disabled={creditLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreditOperation}
                            disabled={creditLoading || !creditAmount || !creditReason}
                        >
                            {creditLoading ? 'Processing...' : creditOperation === 'add' ? 'Add Credits' : 'Deduct Credits'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Account Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
                if (!deleteLoading) {
                    setDeleteDialogOpen(open)
                    if (!open) {
                        setDeleteConfirmation('')
                        setDeleteCheckbox(false)
                        setAccountToDelete(null)
                        setDeletionStats(null)
                    }
                }
            }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="h-5 w-5" />
                            Permanently Delete Account
                        </DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. This will permanently delete the account and all associated data.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {accountToDelete && (
                        <div className="space-y-4">
                            {/* Account Info */}
                            <Alert>
                                <AlertTitle>Account to be deleted:</AlertTitle>
                                <AlertDescription className="mt-2 space-y-1">
                                    <div><strong>Name:</strong> {accountToDelete.name || 'Unnamed'}</div>
                                    <div><strong>ID:</strong> <code className="text-xs">{accountToDelete.id}</code></div>
                                    <div><strong>Type:</strong> {accountToDelete.personal_account ? 'Personal' : 'Team'}</div>
                                </AlertDescription>
                            </Alert>

                            {/* Deletion Statistics */}
                            {deletionStats && (
                                <Alert variant="destructive">
                                    <AlertTriangle className="h-4 w-4" />
                                    <AlertTitle>Data that will be permanently deleted:</AlertTitle>
                                    <AlertDescription className="mt-2 space-y-1">
                                        <div>• {deletionStats.brands} brand{deletionStats.brands !== 1 ? 's' : ''}</div>
                                        <div>• {deletionStats.campaigns} campaign{deletionStats.campaigns !== 1 ? 's' : ''}</div>
                                        <div>• {deletionStats.contents} content piece{deletionStats.contents !== 1 ? 's' : ''}</div>
                                        {deletionStats.credits > 0 && (
                                            <div>• {deletionStats.credits.toLocaleString()} credit{deletionStats.credits !== 1 ? 's' : ''}</div>
                                        )}
                                        <div>• All user access and permissions</div>
                                        <div>• All billing and subscription data</div>
                                        <div>• All logs and analytics</div>
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Confirmation Checkbox */}
                            <div className="flex items-start space-x-2">
                                <Checkbox
                                    id="delete-confirm-checkbox"
                                    checked={deleteCheckbox}
                                    onCheckedChange={(checked) => setDeleteCheckbox(checked as boolean)}
                                    disabled={deleteLoading}
                                />
                                <label
                                    htmlFor="delete-confirm-checkbox"
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                    I understand that this action is permanent and cannot be undone
                                </label>
                            </div>

                            {/* Type to Confirm */}
                            <div className="space-y-2">
                                <Label htmlFor="delete-confirmation">
                                    Type <strong>{accountToDelete.name || accountToDelete.slug || 'the account name'}</strong> to confirm:
                                </Label>
                                <Input
                                    id="delete-confirmation"
                                    value={deleteConfirmation}
                                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                                    placeholder="Type account name to confirm"
                                    disabled={deleteLoading}
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                            disabled={deleteLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteAccount}
                            disabled={
                                deleteLoading || 
                                !deleteCheckbox || 
                                !deleteConfirmation ||
                                deleteConfirmation.toLowerCase() !== (accountToDelete?.name || accountToDelete?.slug || '').toLowerCase()
                            }
                        >
                            {deleteLoading ? (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Account Permanently
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}